import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { Avatar, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, InputAdornment, LinearProgress, MenuItem, Skeleton, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    Add,
    ArrowDownward,
    ArrowUpward,
    CheckCircle,
    DoneAll,
    Download,
    FilterAlt,
    Paid,
    ReceiptLong,
    Refresh,
    RestartAlt,
    Search,
    TrendingDown,
    Visibility,
    WarningAmber,
    DirectionsBoat,
    LocalShipping,
    Flight,
    Inventory2,
    Analytics,
} from "@mui/icons-material";

import FinanceStatusChip from "../components/FinanceStatusChip";
import { formatPercent, formatDate } from "../utils/formatter";
import {
    COLORS,
    cardSx,
    FinanceCard,
    FinanceSectionHeader,
    FinanceKpiTile,
    FinanceHeroStat,
    SPACE,
    FinanceDetailsSection,
} from "../shared/FinanceUiKit";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appAlert } from "components/AppDialog";
// Koyu hero bandı için (yedekten geri yüklendi)
import { WorkspacePremium } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const getValue = (row, camel, snake, fallback = "") =>
    row?.[camel] ?? row?.[snake] ?? fallback;

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );

    return toNumber(value);
};

const getBuyingRate = (row) => {
    if (isReturnInvoice(row)) return 0;

    return firstDefinedNumber(
        row?.buyingRate,
        row?.buying_rate,
        row?.financialSummary?.buyingRate,
        row?.financial_summary?.buying_rate,
        row?.freightCharges?.buyingRate,
        row?.freight_charges?.buying_rate,
    );
};

const getOperationalCost = (row) => {
    const summary = row?.financialSummary || row?.financial_summary || {};

    const freightCharges = row?.freightCharges || row?.freight_charges || {};

    const explicitOperationalCost = firstDefinedNumber(
        row?.operationalCost,
        row?.operational_cost,
        summary?.operationalCost,
        summary?.operational_cost,
    );

    if (explicitOperationalCost > 0) {
        return explicitOperationalCost;
    }

    const roadCost =
        firstDefinedNumber(summary?.shipmentCostTotal) +
        firstDefinedNumber(summary?.convertedLinehaulCost) +
        firstDefinedNumber(summary?.convertedHandlingCost);

    const oceanCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.oceanFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const airCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.airFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const freightChargeCost =
        firstDefinedNumber(freightCharges?.pickupTotal) +
        firstDefinedNumber(freightCharges?.othc?.total) +
        firstDefinedNumber(freightCharges?.oceanFreight?.total) +
        firstDefinedNumber(freightCharges?.airFreight?.total) +
        firstDefinedNumber(freightCharges?.dthc?.total) +
        firstDefinedNumber(freightCharges?.handling?.total) +
        firstDefinedNumber(freightCharges?.deliveryTotal);

    if (roadCost > 0) return roadCost;
    if (oceanCost > 0) return oceanCost;
    if (airCost > 0) return airCost;
    if (freightChargeCost > 0) return freightChargeCost;

    return 0;
};

const getMasterTotalCost = (row) => {
    if (isReturnInvoice(row)) return 0;

    const explicitTotalCost = firstDefinedNumber(
        row?.actualCostTotal,
        row?.actual_cost_total,
        row?.totalCost,
        row?.total_cost,
        row?.financialSummary?.totalCost,
        row?.financial_summary?.total_cost,
    );

    if (explicitTotalCost > 0) {
        // Backend tarafindan hesaplanmis toplam maliyet nihai degerdir.
        // Buying rate'i tekrar eklemek ayni maliyeti iki kez sayar.
        return explicitTotalCost;
    }

    return getOperationalCost(row) + getBuyingRate(row);
};

const getFinanceJobKey = (row, index = 0) =>
    getEntityId(
        row?.financeJobId ||
        row?.finance_job_id ||
        row?.financeJob?._id ||
        row?.finance_job?._id ||
        row?.shipmentId ||
        row?.shipment_id,
    ) ||
    String(
        row?.jobNo || row?.job_no || row?.shipmentNo || row?.shipment_no || "",
    ).trim() ||
    `unlinked-${getEntityId(row?._id || row?.id) || index}`;

const getUniqueFinanceJobs = (rows) => {
    const jobs = new Map();

    rows
        .filter((row) => !isReturnInvoice(row))
        .forEach((row, index) => {
            const key = getFinanceJobKey(row, index);
            const current = jobs.get(key);

            // Ayni Finance Job birden fazla maliyet satirinda tekrar edebilir.
            // Ortak master cost icin en dolu/yuksek kaydi temsilci tutuyoruz.
            if (!current || getMasterTotalCost(row) > getMasterTotalCost(current)) {
                jobs.set(key, row);
            }
        });

    return Array.from(jobs.values());
};

const getSupplierCostContribution = (row) => {
    if (isReturnInvoice(row)) {
        return String(getReturnStatus(row)).toLowerCase() === "issued"
            ? getActualAmount(row)
            : 0;
    }

    const isIncludedInMaster =
        row?.includedInMasterCost === true || row?.included_in_master_cost === true;

    if (isIncludedInMaster) {
        return 0;
    }

    const approvalStatus = String(
        getValue(row, "approvalStatus", "approval_status", ""),
    ).toLowerCase();

    const amount = getActualAmount(row) || getExpectedAmount(row);

    return approvalStatus === "approved" ? amount : 0;
};

// Currencies offered by the "Display Currency" selector -- the currency every
// AGGREGATED total (KPI tiles, hero variance card, transport-mode cards, mode
// chart, footer chips, CSV export) is converted into before it is shown.
// Not to be confused with the record-level `currencyFilter`, which only decides
// which cost lines are listed and never changes how totals are labelled.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();
    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";
    return code;
};

const getCurrency = (row) =>
    normalizeCurrencyCode(
        row?.costCurrency ||
        row?.cost_currency ||
        row?.currency ||
        row?.expectedCurrency ||
        row?.expected_currency ||
        row?.financialSummary?.costCurrency ||
        row?.financialSummary?.totalCurrency ||
        row?.financial_summary?.total_currency ||
        row?.freightCharges?.totalCurrency ||
        row?.freight_charges?.total_currency ||
        "EUR",
    );

const getShipmentNo = (row) =>
    getValue(row, "jobNo", "job_no") ||
    row?.shipmentNo ||
    row?.shipment_no ||
    row?.invoiceNo ||
    row?.invoice_no ||
    "N/A";

const getSupplierName = (row) =>
    getValue(row, "supplierName", "supplier_name", "N/A");

const getCustomerName = (row) =>
    getValue(row, "customerName", "customer_name", row?.customer || "N/A");

const getEntityId = (value) => {
    if (!value) return "";

    if (typeof value === "object") {
        return getEntityId(value?._id || value?.id || value?.$oid);
    }

    return String(value).trim();
};

const getDocumentType = (row) =>
    String(row?.documentType || row?.document_type || "invoice")
        .trim()
        .toLowerCase() === "return_invoice"
        ? "return_invoice"
        : "invoice";

const isReturnInvoice = (row) => getDocumentType(row) === "return_invoice";

const getReturnStatus = (row) =>
    String(row?.returnStatus || row?.return_status || "Draft").trim();

const getOriginalInvoiceId = (row) =>
    getEntityId(
        row?.originalInvoiceId ||
        row?.original_invoice_id ||
        row?.originalInvoice?._id ||
        row?.originalInvoice?.id,
    );

const getOriginalInvoiceNo = (row) =>
    row?.originalInvoiceNumber ||
    row?.original_invoice_number ||
    row?.originalInvoiceNo ||
    row?.original_invoice_no ||
    row?.originalInvoice?.invoiceNo ||
    row?.originalInvoice?.invoice_no ||
    "";

const getDateValue = (row) => {
    const raw =
        row?.invoiceDate ||
        row?.invoice_date ||
        row?.supplierInvoiceDate ||
        row?.supplier_invoice_date ||
        row?.createdAt ||
        row?.created_at ||
        row?.updatedAt ||
        row?.updated_at ||
        row?.dueDate ||
        row?.due_date;

    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const getLinkedReturnInvoices = (row) => {
    const candidates = [
        row?.returnInvoices,
        row?.return_invoices,
        row?.linkedReturnInvoices,
        row?.linked_return_invoices,
    ];

    return candidates.find(Array.isArray) || [];
};

const getReturnDate = (row) => {
    const date = getDateValue(row);
    return date ? date.getTime() : 0;
};

const getRawReturnAmount = (row) =>
    Math.abs(
        firstDefinedNumber(
            row?.actualInvoiceAmount,
            row?.actual_invoice_amount,
            row?.returnedAmount,
            row?.returned_amount,
            row?.totalAmount,
            row?.total_amount,
            row?.invoiceSalesAmount,
            row?.amount,
        ),
    );

const getReturnSummary = (row) => {
    if (isReturnInvoice(row)) {
        return {
            status: getReturnStatus(row),
            amount: getRawReturnAmount(row),
            count: 1,
            latestReturnInvoice: row,
        };
    }

    const allReturns = getLinkedReturnInvoices(row);
    const activeReturns = allReturns.filter(
        (item) => String(getReturnStatus(item)).toLowerCase() !== "cancelled",
    );
    const issuedReturns = activeReturns.filter(
        (item) => String(getReturnStatus(item)).toLowerCase() === "issued",
    );
    const latestActiveReturn =
        [...activeReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const latestReturnInvoice =
        [...allReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const issuedAmount = issuedReturns.reduce(
        (sum, item) => sum + getRawReturnAmount(item),
        0,
    );
    const activeAmount = activeReturns.reduce(
        (sum, item) => sum + getRawReturnAmount(item),
        0,
    );
    const originalAmount = Math.abs(
        firstDefinedNumber(
            allReturns[0]?.originalInvoiceTotal,
            allReturns[0]?.original_invoice_total,
            row?.originalInvoiceTotal,
            row?.original_invoice_total,
            row?.totalAmount,
            row?.total_amount,
            row?.actualInvoiceAmount,
            row?.actual_invoice_amount,
            row?.amount,
        ),
    );
    const latestStatus = latestActiveReturn
        ? String(getReturnStatus(latestActiveReturn)).toLowerCase()
        : "";

    let status = "No Return";
    if (latestStatus === "draft") status = "Return Draft";
    else if (latestStatus === "approved") status = "Return Approved";
    else if (
        issuedAmount > 0 &&
        originalAmount > 0 &&
        issuedAmount >= originalAmount - 0.01
    ) {
        status = "Full Return";
    } else if (issuedAmount > 0) status = "Partial Return";
    else if (activeAmount > 0) status = "Return Pending";
    else if (allReturns.length > 0) status = "Return Cancelled";

    return {
        status,
        amount: activeAmount,
        count: allReturns.length,
        latestReturnInvoice,
    };
};

const RETURN_STATUS_STYLES = {
    "No Return": { color: "#475467", background: "#F2F4F7", border: "#D0D5DD" },
    Draft: { color: "#B54708", background: "#FFFAEB", border: "#FEDF89" },
    "Return Draft": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    Approved: { color: "#175CD3", background: "#EFF8FF", border: "#B2DDFF" },
    "Return Approved": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    Issued: { color: "#027A48", background: "#ECFDF3", border: "#ABEFC6" },
    "Partial Return": {
        color: "#027A48",
        background: "#ECFDF3",
        border: "#ABEFC6",
    },
    "Full Return": { color: "#6941C6", background: "#F4F3FF", border: "#D9D6FE" },
    "Return Pending": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    Cancelled: { color: "#B42318", background: "#FEF3F2", border: "#FECDCA" },
    "Return Cancelled": {
        color: "#B42318",
        background: "#FEF3F2",
        border: "#FECDCA",
    },
};

const normalizeCountry = (value) =>
    String(value || "")
        .trim()
        .toLocaleUpperCase("tr-TR")
        .replace(/İ/g, "I")
        .replace(/Ü/g, "U");
const normalizeLookupKey = (value) => {
    if (!value) return "";
    if (typeof value === "object")
        return normalizeLookupKey(
            value?._id ||
            value?.id ||
            value?.$oid ||
            value?.nameKey ||
            value?.name ||
            value?.companyName,
        );
    return String(value)
        .trim()
        .toLocaleUpperCase("tr-TR")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
};
const getDirectCountries = (item) =>
    [
        item,
        item?.customer,
        item?.serviceProvider,
        item?.supplier,
        item?.globalAgent,
        item?.originalInvoice,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .filter((value) => value && typeof value === "object")
        .flatMap((value) => [
            value?.customerCountry,
            value?.serviceProviderCountry,
            value?.globalAgentCountry,
            ...(Array.isArray(value?.servicesCountry) ? value.servicesCountry : []),
        ])
        .filter(Boolean);
const getPartyKeys = (item) =>
    [
        item?.customerId,
        item?.customer,
        item?.customerName,
        item?.customer_name,
        item?.serviceProviderId,
        item?.serviceProvider,
        item?.serviceProviderName,
        item?.supplierId,
        item?.supplier,
        item?.supplierName,
        item?.supplier_name,
        item?.globalAgentId,
        item?.globalAgent,
        item?.globalAgentName,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
        item?.originalInvoice?.customerId,
        item?.originalInvoice?.customerName,
        item?.originalInvoice?.supplierId,
        item?.originalInvoice?.supplierName,
    ]
        .map(normalizeLookupKey)
        .filter(Boolean);
const getGeographicScope = (item, lookup = new Map()) => {
    const countries = [
        ...getDirectCountries(item),
        ...getPartyKeys(item).flatMap((key) => lookup.get(key) || []),
    ];
    return countries.some((country) =>
        ["TURKEY", "TURKIYE"].includes(normalizeCountry(country)),
    )
        ? "DOMESTIC"
        : "INTERNATIONAL";
};
const getGeographicLabel = (item, lookup) =>
    getGeographicScope(item, lookup) === "DOMESTIC"
        ? "Domestic"
        : "International";

const getCostItem = (row) =>
    getValue(row, "costItemName", "cost_item_name", "Cost Line");

const getRawExpectedAmount = (row) =>
    Number(getValue(row, "expectedAmount", "expected_amount", 0));

const getExpectedAmount = (row) =>
    isReturnInvoice(row) ? 0 : getRawExpectedAmount(row);

const getRawActualAmount = (row) =>
    firstDefinedNumber(
        row?.actualInvoiceAmount,
        row?.actual_invoice_amount,
        isReturnInvoice(row) ? row?.returnedAmount : undefined,
        isReturnInvoice(row) ? row?.returned_amount : undefined,
        isReturnInvoice(row) ? row?.totalAmount : undefined,
        isReturnInvoice(row) ? row?.amount : undefined,
        getRawExpectedAmount(row),
    );

const getActualAmount = (row) => {
    const amount = Math.abs(getRawActualAmount(row));

    if (!isReturnInvoice(row)) return getRawActualAmount(row);
    if (String(getReturnStatus(row)).toLowerCase() !== "issued") return 0;

    return -amount;
};

const getDisplayActualAmount = (row) =>
    isReturnInvoice(row)
        ? -Math.abs(getRawActualAmount(row))
        : getRawActualAmount(row);

const getVarianceAmount = (row) =>
    Number(
        getValue(
            row,
            "varianceAmount",
            "variance_amount",
            getActualAmount(row) - getExpectedAmount(row),
        ),
    );

const getVariancePercent = (row) => {
    const expected = getExpectedAmount(row);
    return expected ? (getVarianceAmount(row) / expected) * 100 : 0;
};

const getToleranceStatus = (row) => {
    if (isReturnInvoice(row)) return "Return Adjustment";

    const variancePercent = Math.abs(getVariancePercent(row));
    const limit = Number(
        getValue(row, "toleranceLimitPercent", "tolerance_limit_percent", 5),
    );

    if (variancePercent > limit) return "Tolerance Exceeded";

    return getValue(
        row,
        "toleranceStatus",
        "tolerance_status",
        "Within Tolerance",
    );
};

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(Number(value || 0));

const formatNumber = (value) =>
    new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
    }).format(Number(value || 0));

const normalizeText = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[ıİ]/g, "i");

const getInvoiceOrigin = (item) =>
    normalizeText(
        item?.invoiceOrigin ||
        item?.invoice_origin ||
        item?.originalInvoice?.invoiceOrigin ||
        item?.originalInvoice?.invoice_origin ||
        "tms",
    ) === "manual"
        ? "manual"
        : "tms";

const getTransportMode = (item) => {
    if (getInvoiceOrigin(item) === "manual") {
        return "manual";
    }

    const candidates = [
        item?.sourceType,
        item?.source_type,
        item?.transportMode,
        item?.transport_mode,
        item?.shipmentMode,
        item?.shipment_mode,
        item?.productType,
        item?.product_type,
        item?.serviceType,
        item?.service_type,
        item?.tmsSource,
        item?.tms_source,
        item?.module,
        item?.source,
        item?.originalInvoice?.sourceType,
        item?.originalInvoice?.transportMode,
        item?.originalInvoice?.productType,
        item?.originalInvoice?.serviceType,
        getShipmentNo(item),
    ]
        .map(normalizeText)
        .join(" ");

    if (
        candidates.includes("ocean") ||
        candidates.includes("sea") ||
        candidates.includes("fcl") ||
        candidates.includes("lcl") ||
        candidates.includes("ohfl") ||
        candidates.includes("ombl")
    ) {
        return "ocean";
    }

    if (
        candidates.includes("air") ||
        candidates.includes("flight") ||
        candidates.includes("ahfl") ||
        candidates.includes("ambl")
    ) {
        return "air";
    }

    return "road";
};

const getShipmentType = (item) => {
    const raw =
        item?.shipmentType ||
        item?.shipment_type ||
        item?.transportType ||
        item?.transport_type ||
        item?.oceanTransportType ||
        item?.ocean_transport_type ||
        item?.airTransportType ||
        item?.air_transport_type ||
        item?.serviceType ||
        item?.service_type ||
        item?.originalInvoice?.shipmentType ||
        item?.originalInvoice?.transportType ||
        item?.originalInvoice?.oceanTransportType ||
        item?.originalInvoice?.airTransportType ||
        item?.originalInvoice?.serviceType ||
        "General";

    return String(raw).trim() || "General";
};

const MODE_CONFIG = {
    ocean: {
        label: "Ocean",
        color: "#1570EF",
        light: "#EFF8FF",
        border: "#B2DDFF",
        icon: DirectionsBoat,
    },
    road: {
        label: "Road",
        color: "#B54708",
        light: "#FFFAEB",
        border: "#FEDF89",
        icon: LocalShipping,
    },
    air: {
        label: "Air",
        color: "#6941C6",
        light: "#F4F3FF",
        border: "#D9D6FE",
        icon: Flight,
    },
    manual: {
        label: "Manual",
        color: "#475467",
        light: "#F2F4F7",
        border: "#D0D5DD",
        icon: ReceiptLong,
    },
};

const TransportCostCard = ({ mode, data, totalRecords, currency = "EUR" }) => {
    const config = MODE_CONFIG[mode] || MODE_CONFIG.manual;
    const Icon = config.icon;
    const share = totalRecords ? (data.count / totalRecords) * 100 : 0;
    const variancePositive = data.variance <= 0;

    return (
        <Card sx={{ ...cardSx, height: "100%" }}>
            <MDBox p={2.4}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <MDBox>
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: COLORS.subtext, letterSpacing: ".08em" }}
                        >
                            {`${financeText(config.label).toUpperCase()} ${financeText("SUPPLIER COSTS")}`}
                        </MDTypography>
                        <MDTypography
                            variant="h4"
                            fontWeight="bold"
                            mt={0.8}
                            sx={{ color: COLORS.text }}
                        >
                            {formatNumber(data.count)}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {share.toFixed(1)}% {financeText("of filtered records")}
                        </MDTypography>
                    </MDBox>
                    <MDBox
                        width={46}
                        height={46}
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            color: config.color,
                            background: config.light,
                            border: `1px solid ${config.border}`,
                        }}
                    >
                        <Icon />
                    </MDBox>
                </MDBox>

                <Divider sx={{ my: 1.7 }} />

                <Grid container spacing={1.2}>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Expected")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.amber }}
                        >
                            {formatCompact(data.expected, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Actual")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.red }}
                        >
                            {formatCompact(data.actual, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Variance")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: variancePositive ? COLORS.green : COLORS.red }}
                        >
                            {formatCompact(data.variance, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Approval")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.green }}
                        >
                            {data.approvalRate.toFixed(1)}%
                        </MDTypography>
                    </Grid>
                </Grid>

                <LinearProgress
                    variant="determinate"
                    value={Math.min(Math.max(data.approvalRate, 0), 100)}
                    sx={{
                        mt: 1.7,
                        height: 7,
                        borderRadius: "999px",
                        backgroundColor: "#EEF2F6",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: "999px",
                            background: config.color,
                        },
                    }}
                />
            </MDBox>
        </Card>
    );
};

const ModeCostChart = ({ summary, currency = "EUR" }) => {
    const maxValue = Math.max(
        ...Object.values(summary).flatMap((item) => [item.expected, item.actual]),
        1,
    );

    return (
        <MDBox>
            {Object.entries(summary).map(([mode, data]) => {
                const config = MODE_CONFIG[mode] || MODE_CONFIG.manual;
                const Icon = config.icon;
                return (
                    <MDBox key={mode} mb={2.15}>
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.7}
                        >
                            <MDBox display="flex" alignItems="center" gap={0.8}>
                                <MDBox
                                    width={30}
                                    height={30}
                                    borderRadius="9px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        color: config.color,
                                        background: config.light,
                                        border: `1px solid ${config.border}`,
                                    }}
                                >
                                    <Icon sx={{ fontSize: "16px !important" }} />
                                </MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText(config.label)}
                                </MDTypography>
                            </MDBox>
                            <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                sx={{ color: data.variance <= 0 ? COLORS.green : COLORS.red }}
                            >
                                {financeText("Variance")} {formatCompact(data.variance, currency)}
                            </MDTypography>
                        </MDBox>
                        <MDBox
                            display="grid"
                            gridTemplateColumns="1fr auto"
                            gap={1}
                            alignItems="center"
                            mb={0.45}
                        >
                            <MDBox
                                height={8}
                                borderRadius="999px"
                                sx={{ background: "#EEF2F6", overflow: "hidden" }}
                            >
                                <MDBox
                                    height="100%"
                                    borderRadius="999px"
                                    sx={{
                                        width: `${(data.expected / maxValue) * 100}%`,
                                        background: COLORS.amber,
                                    }}
                                />
                            </MDBox>
                            <MDTypography
                                variant="caption"
                                sx={{ color: COLORS.subtext, minWidth: 74, textAlign: "right" }}
                            >
                                {formatCompact(data.expected, currency)}
                            </MDTypography>
                        </MDBox>
                        <MDBox
                            display="grid"
                            gridTemplateColumns="1fr auto"
                            gap={1}
                            alignItems="center"
                        >
                            <MDBox
                                height={8}
                                borderRadius="999px"
                                sx={{ background: "#EEF2F6", overflow: "hidden" }}
                            >
                                <MDBox
                                    height="100%"
                                    borderRadius="999px"
                                    sx={{
                                        width: `${(data.actual / maxValue) * 100}%`,
                                        background: config.color,
                                    }}
                                />
                            </MDBox>
                            <MDTypography
                                variant="caption"
                                sx={{ color: COLORS.subtext, minWidth: 74, textAlign: "right" }}
                            >
                                {formatCompact(data.actual, currency)}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                );
            })}
            <MDBox display="flex" gap={2} mt={1}>
                <MDBox display="flex" alignItems="center" gap={0.6}>
                    <MDBox
                        width={10}
                        height={10}
                        borderRadius="3px"
                        sx={{ background: COLORS.amber }}
                    />
                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                        {financeText("Expected")}
                    </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={0.6}>
                    <MDBox
                        width={10}
                        height={10}
                        borderRadius="3px"
                        sx={{ background: COLORS.blue }}
                    />
                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                        {financeText("Actual")}
                    </MDTypography>
                </MDBox>
            </MDBox>
        </MDBox>
    );
};

const SupplierCosts = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [costLines, setCostLines] = useState([]);
    const [businessPartners, setBusinessPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const [search, setSearch] = useState("");
    const [invoiceStatus, setInvoiceStatus] = useState("all");
    const [approvalStatus, setApprovalStatus] = useState("all");
    const [paymentStatus, setPaymentStatus] = useState("all");
    const [toleranceStatus, setToleranceStatus] = useState("all");
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const todayValue = `${currentYear}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
    const [endDate, setEndDate] = useState(todayValue);
    const [selectedParty, setSelectedParty] = useState(null);
    const [transportMode, setTransportMode] = useState("all");
    const [invoiceOrigin, setInvoiceOrigin] = useState("all");
    const [documentType, setDocumentType] = useState("all");
    const [returnStatus, setReturnStatus] = useState("all");
    const [currencyFilter, setCurrencyFilter] = useState("all");
    const [geographicScope, setGeographicScope] = useState("all");
    const [exchangeRates, setExchangeRates] = useState(null);

    // Display currency for AGGREGATED totals only. This is a genuinely separate
    // control from `currencyFilter` above: `currencyFilter` decides WHICH COST
    // LINES are listed (matched against each line's own currency), while
    // `selectedCurrency` decides WHAT CURRENCY the blended totals are rendered
    // in. They used to be the same thing -- the reporting currency was derived
    // from `currencyFilter` -- so narrowing the table to USD lines also silently
    // relabelled every KPI/chart/CSV total as USD, and there was no way to view
    // mixed-currency totals in a currency of your choosing.
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    const [modalOpen, setModalOpen] = useState(false);

    const [costForm, setCostForm] = useState({
        financeJobId: "",
        shipmentId: "",
        jobNo: "",
        shipmentNo: "",
        supplierName: "",
        supplierType: "Service Provider",
        costItemName: "",
        costItemCode: "",
        serviceType: "",
        expectedAmount: "",
        actualInvoiceAmount: "",
        currency: "EUR",
        toleranceLimitPercent: 5,
        invoiceStatus: "Expected",
        approvalStatus: "Pending",
        paymentStatus: "Not Due",
        dueDate: "",
        includedInMasterCost: false,
    });

    const token = localStorage.getItem("token");

    const fetchWithAuth = useCallback(
        async (path) => {
            const response = await fetch(`${API_URL}${path}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`${path} fetch failed`);
            }

            const data = await response.json();
            const candidates = [
                data,
                data?.data,
                data?.supplierCosts,
                data?.supplier_costs,
                data?.returnInvoices,
                data?.return_invoices,
                data?.costs,
                data?.results,
                data?.records,
                data?.data?.data,
                data?.data?.supplierCosts,
                data?.data?.supplier_costs,
                data?.data?.returnInvoices,
                data?.data?.return_invoices,
                data?.data?.costs,
                data?.data?.results,
                data?.data?.records,
            ];

            return candidates.find(Array.isArray) || [];
        },
        [token],
    );

    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );
                if (!response.ok) throw new Error("Exchange rates could not be loaded");
                const data = await response.json();
                setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rate fetch error:", error);
                setExchangeRates(null);
            }
        };

        loadExchangeRates();
    }, []);

    // Converts a cost line's amount from the line's own cost currency into the
    // user-selected DISPLAY currency. Returns null when a rate is missing --
    // callers must treat null as "can't convert" rather than silently computing
    // a wrong number.
    const convertCostAmount = useCallback(
        (item, amount, targetCurrency = selectedCurrency) => {
            const value = Number(amount) || 0;
            const from = getCurrency(item);
            const to = normalizeCurrencyCode(targetCurrency);

            if (from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates, selectedCurrency],
    );

    const fetchSupplierCosts = useCallback(async () => {
        try {
            setLoading(true);

            const [supplierCostData, manualPurchaseInvoices, returnInvoiceData] =
                await Promise.all([
                    fetchWithAuth("/finance/supplier-costs").catch((financeError) => {
                        console.warn("Supplier costs endpoint failed:", financeError);
                        return [];
                    }),
                    fetchWithAuth(
                        "/finance/manual-invoices?invoiceType=purchase&limit=200",
                    ).catch((manualInvoiceError) => {
                        console.warn(
                            "Manual purchase invoices could not be loaded:",
                            manualInvoiceError,
                        );
                        return [];
                    }),
                    fetchWithAuth("/finance/return-invoices?invoiceType=purchase").catch(
                        (returnInvoiceError) => {
                            console.warn(
                                "Purchase return invoices could not be loaded:",
                                returnInvoiceError,
                            );
                            return [];
                        },
                    ),
                ]);

            const originalPurchaseInvoiceMap = new Map();

            manualPurchaseInvoices
                .filter(
                    (invoice) =>
                        !isReturnInvoice(invoice) &&
                        (!invoice?.invoiceType ||
                            String(invoice.invoiceType).toLowerCase() === "purchase"),
                )
                .forEach((invoice) => {
                    const invoiceId = getEntityId(
                        invoice?._id || invoice?.id || invoice?.invoiceId,
                    );

                    if (invoiceId) originalPurchaseInvoiceMap.set(invoiceId, invoice);
                });

            const uniqueReturnInvoices = Array.from(
                new Map(
                    returnInvoiceData.map((returnInvoice, index) => [
                        getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                        `purchase-return-${index}`,
                        returnInvoice,
                    ]),
                ).values(),
            );
            const returnsByOriginalId = new Map();

            uniqueReturnInvoices.forEach((returnInvoice) => {
                const originalInvoiceId = getOriginalInvoiceId(returnInvoice);
                if (!originalInvoiceId) return;

                returnsByOriginalId.set(originalInvoiceId, [
                    ...(returnsByOriginalId.get(originalInvoiceId) || []),
                    returnInvoice,
                ]);
            });

            const manualCostLines = Array.from(
                originalPurchaseInvoiceMap.values(),
            ).flatMap((invoice) => {
                const invoiceItems =
                    [invoice?.invoiceItems, invoice?.invoice_items].find(Array.isArray) ||
                    [];
                const lines = invoiceItems.length ? invoiceItems : [{}];
                const invoiceId = getEntityId(invoice?._id || invoice?.id);
                const linkedReturns = returnsByOriginalId.get(invoiceId) || [];

                return lines.map((invoiceItem, index) => {
                    const quantity =
                        firstDefinedNumber(
                            invoiceItem?.unitQuantity,
                            invoiceItem?.unit_quantity,
                            invoiceItem?.quantity,
                        ) || 1;
                    const unitCost = firstDefinedNumber(
                        invoiceItem?.totalCost,
                        invoiceItem?.total_cost,
                        invoiceItem?.unitPrice,
                        invoiceItem?.unit_price,
                        invoiceItem?.amount,
                    );
                    const lineAmount =
                        firstDefinedNumber(invoiceItem?.amount) ||
                        unitCost * quantity ||
                        firstDefinedNumber(
                            invoice?.totalCost,
                            invoice?.actualCostTotal,
                            invoice?.totalAmount,
                        );

                    return {
                        ...invoice,
                        ...invoiceItem,
                        _id: `${invoice?._id || invoice?.id}-item-${index}`,
                        manualInvoiceId: invoice?._id || invoice?.id,
                        documentType: "invoice",
                        returnInvoices: linkedReturns,
                        invoiceOrigin: "manual",
                        invoiceType: "purchase",
                        costItemName:
                            invoiceItem?.commodity ||
                            invoiceItem?.description ||
                            "Manual Purchase Invoice",
                        expectedAmount: lineAmount,
                        actualInvoiceAmount: lineAmount,
                        costCurrency: normalizeCurrencyCode(
                            invoiceItem?.costCurrency ||
                            invoiceItem?.cost_currency ||
                            invoice?.costCurrency ||
                            invoice?.currency,
                        ),
                    };
                });
            });

            const returnCostLines = uniqueReturnInvoices.flatMap((returnInvoice) => {
                const returnInvoiceId = getEntityId(
                    returnInvoice?._id || returnInvoice?.id,
                );
                const originalInvoiceId = getOriginalInvoiceId(returnInvoice);
                const originalInvoice =
                    originalPurchaseInvoiceMap.get(originalInvoiceId) || null;
                const invoiceItems =
                    [returnInvoice?.invoiceItems, returnInvoice?.invoice_items].find(
                        Array.isArray,
                    ) || [];
                const lines = invoiceItems.length ? invoiceItems : [{}];

                return lines.map((invoiceItem, index) => {
                    const quantity =
                        firstDefinedNumber(
                            invoiceItem?.returnedQuantity,
                            invoiceItem?.returned_quantity,
                            invoiceItem?.unitQuantity,
                            invoiceItem?.unit_quantity,
                            invoiceItem?.quantity,
                        ) || 1;
                    const unitPrice = firstDefinedNumber(
                        invoiceItem?.unitPrice,
                        invoiceItem?.unit_price,
                    );
                    const lineAmount =
                        firstDefinedNumber(
                            invoiceItem?.amount,
                            invoiceItem?.lineAmount,
                            invoiceItem?.line_amount,
                        ) ||
                        unitPrice * quantity ||
                        (lines.length === 1 ? getRawReturnAmount(returnInvoice) : 0);

                    return {
                        ...(originalInvoice || {}),
                        ...returnInvoice,
                        ...invoiceItem,
                        _id: `return-${returnInvoiceId || "invoice"}-item-${index}`,
                        returnInvoiceId,
                        originalInvoiceId,
                        originalInvoice,
                        documentType: "return_invoice",
                        manualInvoiceId: originalInvoiceId,
                        invoiceType: "purchase",
                        invoiceOrigin:
                            returnInvoice?.invoiceOrigin ||
                            originalInvoice?.invoiceOrigin ||
                            "manual",
                        shipmentNo:
                            returnInvoice?.shipmentNo || originalInvoice?.shipmentNo,
                        jobNo:
                            returnInvoice?.jobNo ||
                            originalInvoice?.jobNo ||
                            returnInvoice?.shipmentNo ||
                            originalInvoice?.shipmentNo,
                        supplierName:
                            returnInvoice?.supplierName ||
                            originalInvoice?.supplierName ||
                            "N/A",
                        customerName:
                            returnInvoice?.customerName ||
                            originalInvoice?.customerName ||
                            "N/A",
                        costItemName:
                            invoiceItem?.commodity ||
                            invoiceItem?.description ||
                            "Supplier Return",
                        costItemCode:
                            invoiceItem?.itemCode || invoiceItem?.item_code || "RETURN",
                        expectedAmount: 0,
                        actualInvoiceAmount: Math.abs(lineAmount),
                        costCurrency: normalizeCurrencyCode(
                            invoiceItem?.costCurrency ||
                            invoiceItem?.cost_currency ||
                            returnInvoice?.costCurrency ||
                            returnInvoice?.currency ||
                            originalInvoice?.costCurrency ||
                            originalInvoice?.currency,
                        ),
                        currency: normalizeCurrencyCode(
                            returnInvoice?.currency ||
                            returnInvoice?.costCurrency ||
                            originalInvoice?.currency ||
                            originalInvoice?.costCurrency,
                        ),
                        invoiceStatus: "Return Invoice",
                        approvalStatus: getReturnStatus(returnInvoice),
                        paymentStatus: "Not Applicable",
                        toleranceStatus: "Return Adjustment",
                        includedInMasterCost: false,
                    };
                });
            });
            const uniqueCostLines = new Map();

            [...supplierCostData, ...manualCostLines, ...returnCostLines].forEach(
                (line, index) => {
                    const linkedOriginalId = getEntityId(
                        line?.manualInvoiceId || line?.invoiceId || line?.invoice_id,
                    );
                    const normalizedLine = isReturnInvoice(line)
                        ? line
                        : {
                            ...line,
                            documentType: "invoice",
                            returnInvoices: Array.from(
                                new Map(
                                    [
                                        ...getLinkedReturnInvoices(line),
                                        ...(returnsByOriginalId.get(linkedOriginalId) || []),
                                    ].map((returnInvoice, returnIndex) => [
                                        getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                                        `${linkedOriginalId}-return-${returnIndex}`,
                                        returnInvoice,
                                    ]),
                                ).values(),
                            ),
                        };
                    const key =
                        normalizedLine?._id ||
                        normalizedLine?.id ||
                        `${normalizedLine?.invoiceNo || "cost"}-${index}`;
                    uniqueCostLines.set(String(key), normalizedLine);
                },
            );

            setCostLines(Array.from(uniqueCostLines.values()));

            setLastUpdated(new Date());
        } catch (error) {
            console.error("Supplier costs fetch error:", error);
            setCostLines([]);
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const fetchBusinessPartners = useCallback(async () => {
        const firstAvailable = async (paths) => {
            for (const path of paths) {
                try {
                    return await fetchWithAuth(path);
                } catch (error) {
                    console.warn(`${path} lookup failed`, error);
                }
            }
            return [];
        };
        const [customers, providers, agents] = await Promise.all([
            firstAvailable(["/customer", "/customers"]),
            firstAvailable(["/serviceProvider", "/service-provider"]),
            firstAvailable(["/globalAgent", "/global-agent"]),
        ]);
        setBusinessPartners([...customers, ...providers, ...agents]);
    }, [fetchWithAuth]);

    useEffect(() => {
        fetchSupplierCosts();
        fetchBusinessPartners();
    }, [fetchSupplierCosts, fetchBusinessPartners]);

    const partyCountryLookup = useMemo(() => {
        const lookup = new Map();
        businessPartners.forEach((record) => {
            const item = record?.attributes || record;
            const countries = [
                item?.customerCountry,
                item?.serviceProviderCountry,
                item?.globalAgentCountry,
                ...(Array.isArray(item?.servicesCountry) ? item.servicesCountry : []),
            ].filter(Boolean);
            [
                record?.id,
                item?._id,
                item?.id,
                item?.name,
                item?.nameKey,
                item?.companyName,
                item?.customerNo,
                item?.serviceProviderNo,
                item?.globalAgentNo,
            ]
                .map(normalizeLookupKey)
                .filter(Boolean)
                .forEach((key) =>
                    lookup.set(key, [...(lookup.get(key) || []), ...countries]),
                );
        });
        return lookup;
    }, [businessPartners]);

    useEffect(() => {
        const state = location.state || {};

        if (
            state.openCostModal ||
            state.financeJobId ||
            state.shipmentId ||
            state.jobNo ||
            state.shipmentNo
        ) {
            setCostForm((previous) => ({
                ...previous,
                financeJobId: state.financeJobId || previous.financeJobId,
                shipmentId: state.shipmentId || previous.shipmentId,
                jobNo: state.jobNo || state.shipmentNo || previous.jobNo,
                shipmentNo: state.shipmentNo || state.jobNo || previous.shipmentNo,
            }));

            if (state.openCostModal) {
                setModalOpen(true);
            }

            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const statusOptions = useMemo(() => {
        const unique = (camel, snake, fallback) => {
            const values = costLines
                .map((item) => String(getValue(item, camel, snake, fallback)).trim())
                .filter(Boolean);

            return ["all", ...Array.from(new Set(values))];
        };

        return {
            invoice: unique("invoiceStatus", "invoice_status", "Expected"),
            approval: unique("approvalStatus", "approval_status", "Pending"),
            payment: unique("paymentStatus", "payment_status", "Not Due"),
            tolerance: ["all", "Within Tolerance", "Tolerance Exceeded"],
        };
    }, [costLines]);

    const returnStatusOptions = useMemo(
        () => [
            "all",
            ...Array.from(
                new Set(costLines.map((line) => getReturnSummary(line).status)),
            ).filter(Boolean),
        ],
        [costLines],
    );

    const partyOptions = useMemo(() => {
        const unique = new Map();

        costLines.forEach((line) => {
            const customerName = getCustomerName(line);
            const supplierName = getSupplierName(line);

            if (customerName && customerName !== "N/A") {
                unique.set(`CUSTOMER-${customerName}`, {
                    type: "CUSTOMER",
                    name: customerName,
                    label: `Customer - ${customerName}`,
                });
            }

            if (supplierName && supplierName !== "N/A") {
                unique.set(`SUPPLIER-${supplierName}`, {
                    type: "SUPPLIER",
                    name: supplierName,
                    label: `Supplier - ${supplierName}`,
                });
            }
        });

        return Array.from(unique.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [costLines]);

    const transportModeOptions = useMemo(
        () => Array.from(new Set(costLines.map(getTransportMode))).sort(),
        [costLines],
    );

    const currencyOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    costLines.map((line) => String(getCurrency(line)).toUpperCase()),
                ),
            ).sort(),
        [costLines],
    );

    const filteredCostLines = useMemo(() => {
        const searchText = search.toLowerCase().trim();
        const rangeStart = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const rangeEnd = endDate ? new Date(`${endDate}T23:59:59`) : null;

        return costLines.filter((line) => {
            const jobNo = String(getShipmentNo(line)).toLowerCase();
            const supplier = String(getSupplierName(line)).toLowerCase();
            const customer = String(getCustomerName(line)).toLowerCase();
            const lineDate = getDateValue(line);
            const service = String(
                getValue(line, "serviceType", "service_type"),
            ).toLowerCase();
            const costItem = String(getCostItem(line)).toLowerCase();
            const originalInvoiceNo = String(
                getOriginalInvoiceNo(line),
            ).toLowerCase();
            const returnReason = String(
                line?.returnReason || line?.return_reason || "",
            ).toLowerCase();

            const currentInvoiceStatus = String(
                getValue(line, "invoiceStatus", "invoice_status", "Expected"),
            ).toLowerCase();

            const currentApprovalStatus = String(
                getValue(line, "approvalStatus", "approval_status", "Pending"),
            ).toLowerCase();

            const currentPaymentStatus = String(
                getValue(line, "paymentStatus", "payment_status", "Not Due"),
            ).toLowerCase();

            const currentToleranceStatus = String(
                getToleranceStatus(line),
            ).toLowerCase();

            const matchesSearch =
                !searchText ||
                jobNo.includes(searchText) ||
                supplier.includes(searchText) ||
                customer.includes(searchText) ||
                service.includes(searchText) ||
                costItem.includes(searchText) ||
                originalInvoiceNo.includes(searchText) ||
                returnReason.includes(searchText);

            const matchesStartDate =
                !rangeStart || (lineDate && lineDate >= rangeStart);
            const matchesEndDate = !rangeEnd || (lineDate && lineDate <= rangeEnd);
            const matchesParty =
                !selectedParty ||
                (selectedParty.type === "CUSTOMER"
                    ? getCustomerName(line) === selectedParty.name
                    : getSupplierName(line) === selectedParty.name);
            const matchesTransportMode =
                transportMode === "all" || getTransportMode(line) === transportMode;
            const matchesInvoiceOrigin =
                invoiceOrigin === "all" || getInvoiceOrigin(line) === invoiceOrigin;
            const matchesDocumentType =
                documentType === "all" || getDocumentType(line) === documentType;
            const matchesReturnStatus =
                returnStatus === "all" ||
                normalizeText(getReturnSummary(line).status) ===
                normalizeText(returnStatus);
            const matchesCurrency =
                currencyFilter === "all" ||
                String(getCurrency(line)).toUpperCase() === currencyFilter;
            const matchesGeographicScope =
                geographicScope === "all" ||
                getGeographicScope(line, partyCountryLookup) === geographicScope;

            const matchesInvoice =
                invoiceStatus === "all" ||
                currentInvoiceStatus === invoiceStatus.toLowerCase();

            const matchesApproval =
                approvalStatus === "all" ||
                currentApprovalStatus === approvalStatus.toLowerCase();

            const matchesPayment =
                paymentStatus === "all" ||
                currentPaymentStatus === paymentStatus.toLowerCase();

            const matchesTolerance =
                toleranceStatus === "all" ||
                currentToleranceStatus === toleranceStatus.toLowerCase();

            return (
                matchesSearch &&
                matchesStartDate &&
                matchesEndDate &&
                matchesParty &&
                matchesTransportMode &&
                matchesInvoiceOrigin &&
                matchesDocumentType &&
                matchesReturnStatus &&
                matchesCurrency &&
                matchesGeographicScope &&
                matchesInvoice &&
                matchesApproval &&
                matchesPayment &&
                matchesTolerance
            );
        });
    }, [
        costLines,
        search,
        invoiceStatus,
        approvalStatus,
        paymentStatus,
        toleranceStatus,
        startDate,
        endDate,
        selectedParty,
        transportMode,
        invoiceOrigin,
        documentType,
        returnStatus,
        currencyFilter,
        geographicScope,
        partyCountryLookup,
    ]);

    const stats = useMemo(() => {
        const uniqueFinanceJobs = getUniqueFinanceJobs(filteredCostLines);
        const regularCostLines = filteredCostLines.filter(
            (item) => !isReturnInvoice(item),
        );
        const returnCostLines = filteredCostLines.filter(isReturnInvoice);
        const expectedTotal = filteredCostLines.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, getExpectedAmount(item)) ?? 0),
            0,
        );

        const actualTotal = filteredCostLines.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, getActualAmount(item)) ?? 0),
            0,
        );

        const variance = actualTotal - expectedTotal;

        const missingCount = regularCostLines.filter((item) =>
            String(getValue(item, "invoiceStatus", "invoice_status", ""))
                .toLowerCase()
                .includes("missing"),
        ).length;

        const exceededCount = regularCostLines.filter((item) =>
            getToleranceStatus(item).toLowerCase().includes("exceeded"),
        ).length;

        const approvedCount = regularCostLines.filter((item) =>
            String(getValue(item, "approvalStatus", "approval_status", ""))
                .toLowerCase()
                .includes("approved"),
        ).length;

        const approvalRate = regularCostLines.length
            ? (approvedCount / regularCostLines.length) * 100
            : 0;

        const approvedAdditionalCost = filteredCostLines.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, getSupplierCostContribution(item)) ?? 0),
            0,
        );

        const includedInMasterCount = filteredCostLines.filter(
            (item) =>
                item?.includedInMasterCost === true ||
                item?.included_in_master_cost === true,
        ).length;

        const masterCostTotal = uniqueFinanceJobs.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, getMasterTotalCost(item)) ?? 0),
            0,
        );

        const buyingRateTotal = uniqueFinanceJobs.reduce(
            (sum, item) => sum + (convertCostAmount(item, getBuyingRate(item)) ?? 0),
            0,
        );

        return {
            count: filteredCostLines.length,
            regularCount: regularCostLines.length,
            returnCount: returnCostLines.length,
            expectedTotal,
            actualTotal,
            variance,
            missingCount,
            exceededCount,
            approvedCount,
            approvalRate,
            approvedAdditionalCost,
            includedInMasterCount,
            masterCostTotal,
            buyingRateTotal,
        };
    }, [filteredCostLines, convertCostAmount]);

    const transportSummary = useMemo(() => {
        const initial = {
            ocean: {
                count: 0,
                expected: 0,
                actual: 0,
                variance: 0,
                approved: 0,
                approvalRate: 0,
                exceeded: 0,
            },
            road: {
                count: 0,
                expected: 0,
                actual: 0,
                variance: 0,
                approved: 0,
                approvalRate: 0,
                exceeded: 0,
            },
            air: {
                count: 0,
                expected: 0,
                actual: 0,
                variance: 0,
                approved: 0,
                approvalRate: 0,
                exceeded: 0,
            },
            manual: {
                count: 0,
                expected: 0,
                actual: 0,
                variance: 0,
                approved: 0,
                approvalRate: 0,
                exceeded: 0,
            },
        };

        filteredCostLines.forEach((item) => {
            const mode = getTransportMode(item);
            const expected = convertCostAmount(item, getExpectedAmount(item)) ?? 0;
            const actual = convertCostAmount(item, getActualAmount(item)) ?? 0;
            const approved = String(
                getValue(item, "approvalStatus", "approval_status", ""),
            )
                .toLowerCase()
                .includes("approved");
            const exceeded = getToleranceStatus(item)
                .toLowerCase()
                .includes("exceeded");

            const target = initial[mode] || initial.manual;

            target.expected += expected;
            target.actual += actual;
            target.variance += actual - expected;
            if (!isReturnInvoice(item)) {
                target.count += 1;
                if (approved) target.approved += 1;
                if (exceeded) target.exceeded += 1;
            }
        });

        Object.values(initial).forEach((item) => {
            item.approvalRate = item.count ? (item.approved / item.count) * 100 : 0;
        });

        return initial;
    }, [filteredCostLines, convertCostAmount]);

    const shipmentTypeSummary = useMemo(() => {
        const counts = {};
        filteredCostLines
            .filter((item) => !isReturnInvoice(item))
            .forEach((item) => {
                const type = getShipmentType(item);
                counts[type] = (counts[type] || 0) + 1;
            });

        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8);
    }, [filteredCostLines]);

    const openReturnInvoice = (returnInvoice) => {
        const returnInvoiceId = getEntityId(
            returnInvoice?.returnInvoiceId || returnInvoice?._id || returnInvoice?.id,
        ).replace(/^return-(.+)-item-\d+$/, "$1");

        if (!returnInvoiceId) {
            appAlert(financeText("Return Invoice ID not found!"));
            return;
        }

        navigate(`/invoice/finance/return-invoices/${returnInvoiceId}`, {
            state: {
                returnInvoice,
                originalInvoice: returnInvoice?.originalInvoice || null,
                originalInvoiceId: getOriginalInvoiceId(returnInvoice),
            },
        });
    };

    const openOriginalInvoice = (returnInvoice) => {
        const originalInvoice = returnInvoice?.originalInvoice || null;
        const originalInvoiceId =
            getOriginalInvoiceId(returnInvoice) ||
            getEntityId(originalInvoice?._id || originalInvoice?.id);

        if (!originalInvoiceId) {
            appAlert(financeText("Original Invoice ID not found!"));
            return;
        }

        navigate(`/invoice/finance/jobs/${originalInvoiceId}`, {
            state: {
                job: originalInvoice,
                invoiceId: originalInvoiceId,
                financeJobId: originalInvoiceId,
                invoiceOrigin:
                    getInvoiceOrigin(originalInvoice || returnInvoice) || "manual",
            },
        });
    };

    const openLatestReturnInvoice = (line) => {
        const latestReturnInvoice = getReturnSummary(line).latestReturnInvoice;

        if (!latestReturnInvoice) {
            appAlert(financeText("No linked return invoice was found."));
            return;
        }

        openReturnInvoice(latestReturnInvoice);
    };

    const resolveSupplierCost = async (line) => {
        if (isReturnInvoice(line)) return line;

        const recordIds = [
            line?.financeJobId,
            line?.finance_job_id,
            line?.jobFinanceId,
            line?.job_finance_id,
            line?._id,
            line?.id,
        ].filter(Boolean);
        const uniqueIds = [...new Set(recordIds.map(String))];

        for (const recordId of uniqueIds) {
            const detailPaths = [
                `/finance/manual-invoices/${recordId}`,
                `/finance/supplier-costs/${recordId}`,
                `/invoice/${recordId}`,
                `/finance/jobs/${recordId}`,
            ];

            for (const path of detailPaths) {
                try {
                    const response = await fetch(`${API_URL}${path}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) continue;

                    const payload = await response.json();
                    const detail =
                        payload?.data?.cost ||
                        payload?.data?.job ||
                        payload?.data?.data ||
                        payload?.cost ||
                        payload?.job ||
                        payload?.data ||
                        payload;

                    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
                        return {
                            ...line,
                            ...detail,
                            invoiceItems:
                                detail?.invoiceItems ||
                                detail?.invoice_items ||
                                line?.invoiceItems ||
                                line?.invoice_items ||
                                [],
                        };
                    }
                } catch (error) {
                    console.warn(`${path} supplier cost detail lookup failed`, error);
                }
            }
        }

        return line;
    };

    const openFinanceJob = async (line) => {
        if (isReturnInvoice(line)) {
            openReturnInvoice(line);
            return;
        }

        const resolvedLine = await resolveSupplierCost(line);
        const manualInvoiceId =
            resolvedLine?.manualInvoiceId ||
            resolvedLine?.manual_invoice_id ||
            resolvedLine?._id ||
            resolvedLine?.id;

        if (getInvoiceOrigin(resolvedLine) === "manual") {
            if (!manualInvoiceId) {
                appAlert(financeText("Manual Invoice ID not found!"));
                return;
            }

            navigate(`/invoice/finance/jobs/${manualInvoiceId}`, {
                state: {
                    job: resolvedLine,
                    financeJobId: manualInvoiceId,
                    invoiceOrigin: "manual",
                },
            });
            return;
        }

        const financeJobId =
            resolvedLine?.financeJobId ||
            resolvedLine?.finance_job_id ||
            resolvedLine?.jobFinanceId ||
            resolvedLine?.job_finance_id;

        if (!financeJobId) {
            appAlert(
                financeText(
                    "Finance Job ID not found. Supplier cost record must be linked with financeJobId."
                ),
            );
            return;
        }

        navigate(`/invoice/finance/jobs/${financeJobId}`, {
            state: {
                job: resolvedLine,
                financeJobId,
            },
        });
    };

    // Lines built from a manual purchase invoice are not SupplierCost documents
    // (their id is "<invoiceId>-item-<n>"); status changes for them go to the
    // manual invoice itself, and apply to every line of that invoice.
    const getManualLineInvoiceId = (line) =>
        line?.manualInvoiceId && String(line?._id || "").includes("-item-")
            ? String(line.manualInvoiceId)
            : "";

    const patchCostLine = async (line, body, errorMessage) => {
        const id = line?._id || line?.id;
        const manualInvoiceId = getManualLineInvoiceId(line);

        const response = await fetch(
            manualInvoiceId
                ? `${API_URL}/finance/manual-invoices/${manualInvoiceId}`
                : `${API_URL}/finance/supplier-costs/${id}`,
            {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            },
        );

        const result = await response.json().catch(() => null);

        if (!response.ok) {
            throw new Error(result?.message || errorMessage);
        }

        const updated = result?.data || result;

        // Merge onto the existing row: the server response has none of the
        // client-side enrichment (linked returnInvoices, documentType), so
        // replacing the row dropped it and the return columns reset.
        setCostLines((previous) =>
            previous.map((item) => {
                if (manualInvoiceId) {
                    return String(item?.manualInvoiceId) === manualInvoiceId &&
                        !isReturnInvoice(item)
                        ? { ...item, ...body }
                        : item;
                }

                return (item?._id || item?.id) === id
                    ? { ...item, ...updated }
                    : item;
            }),
        );
    };

    const approveLine = async (line) => {
        if (isReturnInvoice(line)) {
            openReturnInvoice(line);
            return;
        }

        const id = line?._id || line?.id;

        if (!id || String(id).startsWith("local-")) {
            appAlert(financeText("This cost line is not saved in DB yet."));
            return;
        }

        try {
            await patchCostLine(
                line,
                getManualLineInvoiceId(line)
                    ? { approvalStatus: "Approved" }
                    : {
                        approvalStatus: "Approved",
                        approvedAt: new Date().toISOString(),
                    },
                "Approval update failed",
            );
        } catch (error) {
            console.error("Supplier cost approval error:", error);
            appAlert(error?.message || financeText("Cost approval could not be saved."));
        }
    };

    const markInvoiceReceived = async (line) => {
        if (isReturnInvoice(line)) {
            openReturnInvoice(line);
            return;
        }

        const id = line?._id || line?.id;

        if (!id || String(id).startsWith("local-")) {
            appAlert(financeText("This cost line is not saved in DB yet."));
            return;
        }

        const actualInvoiceAmount =
            getActualAmount(line) || getExpectedAmount(line);

        try {
            await patchCostLine(
                line,
                getManualLineInvoiceId(line)
                    ? { invoiceStatus: "Received" }
                    : {
                        invoiceStatus: "Received",
                        actualInvoiceAmount,
                    },
                "Invoice status update failed",
            );
        } catch (error) {
            console.error("Supplier invoice received error:", error);
            appAlert(error?.message || financeText("Supplier invoice status could not be saved."));
        }
    };

    const openAddCostLine = () => {
        setCostForm({
            financeJobId: location.state?.financeJobId || "",
            shipmentId: location.state?.shipmentId || "",
            jobNo: location.state?.jobNo || location.state?.shipmentNo || "",
            shipmentNo: location.state?.shipmentNo || location.state?.jobNo || "",
            supplierName: "",
            supplierType: "Service Provider",
            costItemName: "",
            costItemCode: "",
            serviceType: "",
            expectedAmount: "",
            actualInvoiceAmount: "",
            currency: "EUR",
            toleranceLimitPercent: 5,
            invoiceStatus: "Expected",
            approvalStatus: "Pending",
            paymentStatus: "Not Due",
            dueDate: "",
            includedInMasterCost: false,
        });

        setModalOpen(true);
    };

    const saveCostLine = async () => {
        if (!costForm.supplierName.trim()) {
            appAlert(financeText("Supplier name is required."));
            return;
        }

        if (!costForm.costItemName.trim()) {
            appAlert(financeText("Cost item is required."));
            return;
        }

        if (!Number(costForm.expectedAmount || 0)) {
            appAlert(financeText("Expected amount is required."));
            return;
        }

        const expectedAmount = Number(costForm.expectedAmount || 0);
        const actualInvoiceAmount = Number(
            costForm.actualInvoiceAmount || expectedAmount,
        );
        const varianceAmount = actualInvoiceAmount - expectedAmount;
        const variancePercent = expectedAmount
            ? (varianceAmount / expectedAmount) * 100
            : 0;

        const payload = {
            ...costForm,
            currency: normalizeCurrencyCode(costForm.currency),
            costCurrency: normalizeCurrencyCode(costForm.currency),
            financeJobId: costForm.financeJobId || null,
            shipmentId: costForm.shipmentId || null,
            jobNo: costForm.jobNo || costForm.shipmentNo,
            shipmentNo: costForm.shipmentNo || costForm.jobNo,
            includedInMasterCost: Boolean(costForm.includedInMasterCost),
            expectedAmount,
            actualInvoiceAmount,
            varianceAmount,
            variancePercent,
            toleranceStatus:
                Math.abs(variancePercent) > Number(costForm.toleranceLimitPercent || 5)
                    ? "Tolerance Exceeded"
                    : "Within Tolerance",
            createdAt: new Date().toISOString(),
        };

        try {
            const response = await fetch(`${API_URL}/finance/supplier-costs`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);

                throw new Error(
                    errorData?.message ||
                    financeText("Supplier cost line could not be saved."),
                );
            }

            const saved = await response.json();
            setCostLines((previous) => [saved?.data || saved, ...previous]);

            setModalOpen(false);
        } catch (error) {
            // Previously a failed save silently added a browser-only
            // "local-" row and closed the dialog, so the user believed the
            // cost was stored while it vanished on the next refresh. Keep
            // the dialog open and say what went wrong instead.
            console.error("Supplier cost save error:", error);

            appAlert(
                error?.message ||
                financeText("Supplier cost line could not be saved."),
            );
        }
    };

    const resetFilters = () => {
        setSearch("");
        setInvoiceStatus("all");
        setApprovalStatus("all");
        setPaymentStatus("all");
        setToleranceStatus("all");
        setStartDate(`${currentYear}-01-01`);
        setEndDate(todayValue);
        setSelectedParty(null);
        setTransportMode("all");
        setInvoiceOrigin("all");
        setDocumentType("all");
        setReturnStatus("all");
        setCurrencyFilter("all");
        setGeographicScope("all");
    };

    const exportCsv = () => {
        const headers = [
            "Shipment",
            "Supplier",
            "Customer",
            "Geographic Scope",
            "Transport Mode",
            "Invoice Origin",
            "Document Type",
            "Original Invoice No",
            "Return Type",
            "Return Status",
            "Return Reason",
            "Return Amount",
            "Cost Currency",
            "Reporting Currency",
            "Cost Item",
            "Expected",
            "Actual",
            "Buying Rate",
            "Master Total Cost",
            "Included In Master Cost",
            "Approved Additional Cost",
            "Variance",
            "Variance %",
            "Tolerance",
            "Invoice Status",
            "Approval Status",
            "Payment Status",
            "Due Date",
        ];

        const rows = filteredCostLines.map((line) => [
            getShipmentNo(line),
            getSupplierName(line),
            getCustomerName(line),
            getGeographicLabel(line, partyCountryLookup),
            MODE_CONFIG[getTransportMode(line)]?.label || getTransportMode(line),
            getInvoiceOrigin(line) === "manual" ? "Manual" : "TMS",
            isReturnInvoice(line) ? "Return Invoice" : "Supplier Cost",
            getOriginalInvoiceNo(line),
            isReturnInvoice(line)
                ? getValue(line, "returnType", "return_type", "")
                : "",
            getReturnSummary(line).status,
            isReturnInvoice(line)
                ? getValue(line, "returnReason", "return_reason", "")
                : "",
            isReturnInvoice(line)
                ? (convertCostAmount(line, getDisplayActualAmount(line)) ??
                    "Rate unavailable")
                : 0,
            getCurrency(line),
            selectedCurrency,
            getCostItem(line),
            convertCostAmount(line, getExpectedAmount(line)) ?? "Rate unavailable",
            convertCostAmount(line, getActualAmount(line)) ?? "Rate unavailable",
            convertCostAmount(line, getBuyingRate(line)) ?? "Rate unavailable",
            convertCostAmount(line, getMasterTotalCost(line)) ?? "Rate unavailable",
            line?.includedInMasterCost === true ||
            line?.included_in_master_cost === true
                ? "Yes"
                : "No",
            convertCostAmount(line, getSupplierCostContribution(line)) ??
            "Rate unavailable",
            convertCostAmount(line, getVarianceAmount(line)) ?? "Rate unavailable",
            `${getVariancePercent(line).toFixed(2)}%`,
            getToleranceStatus(line),
            getValue(line, "invoiceStatus", "invoice_status", "Expected"),
            getValue(line, "approvalStatus", "approval_status", "Pending"),
            getValue(line, "paymentStatus", "payment_status", "Not Due"),
            formatDate(getValue(line, "dueDate", "due_date")),
        ]);

        const csv = [headers, ...rows]
            .map((row) =>
                row
                    .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
                    .join(","),
            )
            .join("\n");

        const blob = new Blob([`\uFEFF${csv}`], {
            type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        const modeName = transportMode === "all" ? "all-modes" : transportMode;
        const scopeName =
            geographicScope === "all" ? "all-scopes" : geographicScope.toLowerCase();
        link.setAttribute(
            "download",
            `supplier-costs-${modeName}-${scopeName}-${todayValue}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("SHIPMENT"),
                    accessor: "jobNo",
                    width: "13%",
                    Cell: ({ row }) => {
                        const line = row.original;

                        const product = getValue(
                            line,
                            "productType",
                            "product_type",
                            "TMS",
                        );

                        const service = getValue(
                            line,
                            "serviceType",
                            "service_type",
                            "General",
                        );

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.2} py={0.35}>
                                <MDBox
                                    width={38}
                                    height={38}
                                    borderRadius="11px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    sx={{
                                        background:
                                            "#EEF4FF",
                                        color: COLORS.blue,
                                        border: "1px solid #D1E0FF",
                                    }}
                                >
                                    <ReceiptLong sx={{ fontSize: "18px !important" }} />
                                </MDBox>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            cursor: "pointer",
                                            color: COLORS.navy,
                                            display: "block",
                                            maxWidth: 145,
                                            "&:hover": {
                                                color: COLORS.blue,
                                            },
                                        }}
                                        onClick={() => openFinanceJob(line)}
                                    >
                                        {getShipmentNo(line)}
                                    </MDTypography>

                                    <MDBox display="flex" alignItems="center" gap={0.5} mt={0.35}>
                                        <Chip
                                            size="small"
                                            label={financeText(
                                                (
                                                    MODE_CONFIG[getTransportMode(line)] ||
                                                    MODE_CONFIG.manual
                                                ).label
                                            )}
                                            sx={{
                                                height: 20,
                                                color: (
                                                    MODE_CONFIG[getTransportMode(line)] ||
                                                    MODE_CONFIG.manual
                                                ).color,
                                                background: (
                                                    MODE_CONFIG[getTransportMode(line)] ||
                                                    MODE_CONFIG.manual
                                                ).light,
                                                border: `1px solid ${(MODE_CONFIG[getTransportMode(line)] || MODE_CONFIG.manual).border}`,
                                                fontSize: "9px",
                                                fontWeight: 800,
                                            }}
                                        />
                                        <MDTypography
                                            variant="caption"
                                            noWrap
                                            sx={{ color: COLORS.subtext, maxWidth: 90 }}
                                        >
                                            {getShipmentType(line) || product || service}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("DOCUMENT TYPE"),
                    accessor: "documentType",
                    width: "11%",
                    Cell: ({ row }) => {
                        const line = row.original;
                        const returnDocument = isReturnInvoice(line);
                        const originalInvoiceNo = getOriginalInvoiceNo(line);

                        return (
                            <MDBox minWidth={130}>
                                <Chip
                                    size="small"
                                    label={financeText(returnDocument ? "Return Invoice" : "Supplier Cost")}
                                    sx={{
                                        color: returnDocument ? COLORS.purple : COLORS.navy,
                                        background: returnDocument ? COLORS.purpleLight : "#EEF2F6",
                                        border: `1px solid ${
                                            returnDocument ? "#D9D6FE" : "#D0D5DD"
                                        }`,
                                        fontWeight: 800,
                                    }}
                                />

                                {returnDocument && originalInvoiceNo && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.45}
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Original")}: {originalInvoiceNo}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("SUPPLIER"),
                    accessor: "supplierName",
                    width: "15%",
                    Cell: ({ row }) => {
                        const name = getSupplierName(row.original);
                        const type = getValue(
                            row.original,
                            "supplierType",
                            "supplier_type",
                            "-",
                        );

                        return (
                            <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                <Avatar
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        background: "#0B1F3A",
                                        color: "#FFFFFF",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        boxShadow: "none",
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        display="block"
                                        sx={{
                                            color: COLORS.text,
                                            maxWidth: 145,
                                        }}
                                    >
                                        {name}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{
                                            color: COLORS.subtext,
                                            maxWidth: 145,
                                        }}
                                    >
                                        {type}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("COST ITEM"),
                    accessor: "costItemName",
                    width: "13%",
                    Cell: ({ row }) => (
                        <MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {getCostItem(row.original)}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {getValue(row.original, "costItemCode", "cost_item_code", "-")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("EXPECTED"),
                    accessor: "expectedAmount",
                    align: "right",
                    width: "10%",
                    Cell: ({ row }) => {
                        const returnDocument = isReturnInvoice(row.original);

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.amber }}
                                >
                                    {formatCompact(
                                        getExpectedAmount(row.original),
                                        getCurrency(row.original),
                                    )}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText(returnDocument ? "No planned cost" : "Planned cost")}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("ACTUAL"),
                    accessor: "actualInvoiceAmount",
                    align: "right",
                    width: "10%",
                    Cell: ({ row }) => {
                        const line = row.original;
                        const returnDocument = isReturnInvoice(line);
                        const issued =
                            String(getReturnStatus(line)).toLowerCase() === "issued";

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color: returnDocument ? COLORS.green : COLORS.red,
                                    }}
                                >
                                    {formatCompact(
                                        getDisplayActualAmount(line),
                                        getCurrency(line),
                                    )}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText(
                                        returnDocument
                                            ? issued
                                                ? "Cost reversal applied"
                                                : "Pending return · not applied"
                                            : "Supplier invoice"
                                    )}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("VARIANCE"),
                    accessor: "varianceAmount",
                    align: "right",
                    width: "11%",
                    Cell: ({ row }) => {
                        const value = getVarianceAmount(row.original);
                        const percent = getVariancePercent(row.original);
                        const positive = value <= 0;

                        return (
                            <MDBox minWidth={110}>
                                <MDBox textAlign="right">
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        sx={{
                                            color: positive ? COLORS.green : COLORS.red,
                                        }}
                                    >
                                        {formatCompact(value, getCurrency(row.original))}
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                        gap={0.35}
                                        mt={0.25}
                                    >
                                        {positive ? (
                                            <ArrowDownward
                                                sx={{
                                                    fontSize: "12px !important",
                                                    color: COLORS.green,
                                                }}
                                            />
                                        ) : (
                                            <ArrowUpward
                                                sx={{
                                                    fontSize: "12px !important",
                                                    color: COLORS.red,
                                                }}
                                            />
                                        )}

                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color: positive ? COLORS.green : COLORS.red,
                                            }}
                                        >
                                            {formatPercent(percent)}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(Math.abs(percent), 100)}
                                    sx={{
                                        mt: 0.7,
                                        height: 5,
                                        borderRadius: "999px",
                                        backgroundColor: "#EEF2F6",
                                        "& .MuiLinearProgress-bar": {
                                            borderRadius: "999px",
                                            background: positive ? COLORS.green : COLORS.red,
                                        },
                                    }}
                                />
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("MASTER COST"),
                    accessor: "includedInMasterCost",
                    width: "10%",
                    Cell: ({ row }) => {
                        if (isReturnInvoice(row.original)) {
                            return (
                                <Chip
                                    size="small"
                                    label={financeText("Return Adjustment")}
                                    sx={{
                                        color: COLORS.green,
                                        background: COLORS.greenLight,
                                        border: "1px solid #ABEFC6",
                                        fontWeight: 700,
                                        borderRadius: "8px",
                                    }}
                                />
                            );
                        }

                        const included =
                            row.original?.includedInMasterCost === true ||
                            row.original?.included_in_master_cost === true;

                        return (
                            <Chip
                                size="small"
                                label={financeText(included ? "Included" : "Additional")}
                                sx={{
                                    color: included ? COLORS.blue : COLORS.purple,
                                    background: included ? COLORS.blueLight : COLORS.purpleLight,
                                    fontWeight: 700,
                                    borderRadius: "8px",
                                }}
                            />
                        );
                    },
                },
                {
                    Header: financeText("TOLERANCE"),
                    accessor: "toleranceStatus",
                    width: "10%",
                    Cell: ({ row }) =>
                        isReturnInvoice(row.original) ? (
                            <Chip
                                size="small"
                                label={financeText("Return Adjustment")}
                                sx={{
                                    color: COLORS.green,
                                    background: COLORS.greenLight,
                                    border: "1px solid #ABEFC6",
                                    fontWeight: 700,
                                }}
                            />
                        ) : (
                            <FinanceStatusChip status={getToleranceStatus(row.original)} />
                        ),
                },
                {
                    Header: financeText("INVOICE"),
                    accessor: "invoiceStatus",
                    width: "9%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={getValue(
                                row.original,
                                "invoiceStatus",
                                "invoice_status",
                                "Expected",
                            )}
                        />
                    ),
                },
                {
                    Header: financeText("APPROVAL"),
                    accessor: "approvalStatus",
                    width: "9%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={getValue(
                                row.original,
                                "approvalStatus",
                                "approval_status",
                                "Pending",
                            )}
                        />
                    ),
                },
                {
                    Header: financeText("RETURN STATUS"),
                    accessor: "returnStatus",
                    width: "12%",
                    Cell: ({ row }) => {
                        const summary = getReturnSummary(row.original);
                        const style =
                            RETURN_STATUS_STYLES[summary.status] ||
                            RETURN_STATUS_STYLES["No Return"];

                        return (
                            <MDBox minWidth={125}>
                                <Chip
                                    size="small"
                                    label={financeText(summary.status)}
                                    sx={{
                                        color: style.color,
                                        background: style.background,
                                        border: `1px solid ${style.border}`,
                                        fontWeight: 800,
                                    }}
                                />
                                {summary.count > 0 && !isReturnInvoice(row.original) && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.45}
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {summary.count} {financeText(summary.count === 1 ? "document" : "documents")}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("PAYMENT"),
                    accessor: "paymentStatus",
                    width: "9%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={getValue(
                                row.original,
                                "paymentStatus",
                                "payment_status",
                                "Not Due",
                            )}
                        />
                    ),
                },
                {
                    Header: financeText("DUE DATE"),
                    accessor: "dueDate",
                    width: "9%",
                    Cell: ({ row }) => (
                        <MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {formatDate(getValue(row.original, "dueDate", "due_date"))}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {financeText("Supplier maturity")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("ACTIONS"),
                    accessor: "actions",
                    align: "right",
                    width: "9%",
                    Cell: ({ row }) => {
                        const line = row.original;
                        const returnDocument = isReturnInvoice(line);
                        const hasLinkedReturns =
                            !returnDocument && getReturnSummary(line).count > 0;

                        const isApproved =
                            String(
                                getValue(line, "approvalStatus", "approval_status", ""),
                            ).toLowerCase() === "approved";

                        const isReceived =
                            String(
                                getValue(line, "invoiceStatus", "invoice_status", ""),
                            ).toLowerCase() === "received";

                        return (
                            <MDBox display="flex" justifyContent="flex-end" gap={0.55}>
                                <Tooltip
                                    title={
                                        returnDocument
                                            ? financeText("View return invoice")
                                            : financeText("View finance job")
                                    }
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => openFinanceJob(line)}
                                        sx={{
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            border: "1px solid #B2DDFF",
                                            "&:hover": {
                                                background: "#D1E9FF",
                                            },
                                        }}
                                    >
                                        <Visibility sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>

                                {returnDocument && (
                                    <Tooltip title={financeText("Open original invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openOriginalInvoice(line)}
                                            sx={{
                                                color: COLORS.navy,
                                                background: "#EEF2F6",
                                                border: "1px solid #D0D5DD",
                                                "&:hover": { background: "#E4E7EC" },
                                            }}
                                        >
                                            <ReceiptLong sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {hasLinkedReturns && (
                                    <Tooltip title={financeText("Open latest return invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openLatestReturnInvoice(line)}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                border: "1px solid #D9D6FE",
                                                "&:hover": { background: "#E9E7FD" },
                                            }}
                                        >
                                            <ReceiptLong sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {!returnDocument && (
                                    <>
                                        <Tooltip
                                            title={isApproved ? financeText("Already approved") : financeText("Approve cost")}
                                            arrow
                                        >
                      <span>
                        <IconButton
                            size="small"
                            disabled={isApproved}
                            onClick={() => approveLine(line)}
                            sx={{
                                color: COLORS.green,
                                background: COLORS.greenLight,
                                border: "1px solid #ABEFC6",
                                "&:hover": {
                                    background: "#D1FADF",
                                },
                                "&.Mui-disabled": {
                                    background: "#F2F4F7",
                                    borderColor: "#E4E7EC",
                                },
                            }}
                        >
                          <DoneAll sx={{ fontSize: "17px !important" }} />
                        </IconButton>
                      </span>
                                        </Tooltip>

                                        <Tooltip
                                            title={
                                                isReceived
                                                    ? financeText("Invoice already received")
                                                    : financeText("Mark supplier invoice received")
                                            }
                                            arrow
                                        >
                      <span>
                        <IconButton
                            size="small"
                            disabled={isReceived}
                            onClick={() => markInvoiceReceived(line)}
                            sx={{
                                color: COLORS.purple,
                                background: COLORS.purpleLight,
                                border: "1px solid #D9D6FE",
                                "&:hover": {
                                    background: "#E9E7FD",
                                },
                                "&.Mui-disabled": {
                                    background: "#F2F4F7",
                                    borderColor: "#E4E7EC",
                                },
                            }}
                        >
                          <ReceiptLong sx={{ fontSize: "17px !important" }} />
                        </IconButton>
                      </span>
                                        </Tooltip>
                                    </>
                                )}
                            </MDBox>
                        );
                    },
                },
            ],
            rows: filteredCostLines,
        }),
        [filteredCostLines, financeLanguage],
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",
                        color: COLORS.white,
                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                        overflow: "hidden",
                        position: "relative",
                        mb: 3,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 360,
                            height: 360,
                            borderRadius: "50%",
                            right: -130,
                            top: -180,
                            background: "rgba(255,255,255,.07)",
                        },
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox p={{ xs: 2.5, md: 3.5 }} position="relative" zIndex={1}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={7}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background: "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <WorkspacePremium
                                            sx={{
                                                color: "#FFFFFF",
                                                fontSize: 20,
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",
                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE SUPPLIER COST CONTROL")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color: "#FFFFFF",
                                        lineHeight: 1.12,
                                    }}
                                >
                                    {financeText("Supplier Costs")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color: "rgba(255,255,255,.72)",
                                        maxWidth: 720,
                                    }}
                                >
                                    {financeText(
                                        "Manage expected supplier costs, actual invoices, variances, tolerance controls, approvals and payment readiness from one banking-grade workspace."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <CheckCircle
                                                sx={{
                                                    color: "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${stats.regularCount} ${financeText("cost records")} · ${stats.returnCount} ${financeText("return lines")}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color: "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",
                                        border: "1px solid rgba(255,255,255,.13)",
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color: "rgba(255,255,255,.72)",
                                            }}
                                        >
                                            {financeText("COST VARIANCE")}
                                        </MDTypography>

                                        <TrendingDown
                                            sx={{
                                                color: stats.variance > 0 ? "#FECDCA" : "#75E0A7",
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{
                                            color: stats.variance > 0 ? "#FECDCA" : "#FFFFFF",
                                        }}
                                    >
                                        {formatCompact(stats.variance, selectedCurrency)}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(255,255,255,.68)",
                                        }}
                                    >
                                        {financeText("Approval rate")} {stats.approvalRate.toFixed(1)}%{" "}
                                        {financeText("across current filter result")}
                                    </MDTypography>

                                    <MDBox display="flex" gap={1} mt={2} alignItems="stretch">
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {[
                                                { value: "en", label: "EN", flag: "/flags/gb.svg", title: "English" },
                                                { value: "tr", label: "TR", flag: "/flags/tr.svg", title: "Türkçe" },
                                            ].map((language) => (
                                                <MDBox
                                                    key={language.value}
                                                    component="button"
                                                    type="button"
                                                    onClick={() =>
                                                        changeFinanceLanguage({
                                                            target: { value: language.value },
                                                        })
                                                    }
                                                    title={language.title}
                                                    sx={{
                                                        appearance: "none",
                                                        WebkitAppearance: "none",
                                                        border: "none",
                                                        outline: "none",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: "7px",
                                                        height: 38,
                                                        minWidth: 76,
                                                        px: 1.15,
                                                        borderRadius: "8px",
                                                        background:
                                                            financeLanguage === language.value
                                                                ? "#FFFFFF"
                                                                : "transparent",
                                                        color:
                                                            financeLanguage === language.value
                                                                ? COLORS.navy
                                                                : "#FFFFFF",
                                                        boxShadow:
                                                            financeLanguage === language.value
                                                                ? "0 3px 10px rgba(0,0,0,.18)"
                                                                : "none",
                                                    }}
                                                >
                                                    <MDBox
                                                        component="img"
                                                        src={language.flag}
                                                        alt={language.title}
                                                        sx={{
                                                            width: 24,
                                                            height: 17,
                                                            objectFit: "cover",
                                                            borderRadius: "3px",
                                                            display: "block",
                                                        }}
                                                    />
                                                    <MDTypography
                                                        component="span"
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 800,
                                                            color: "inherit",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {language.label}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={openAddCostLine}
                                            sx={{
                                                background: "#FFFFFF !important",
                                                color: `${COLORS.navy} !important`,
                                                flex: 1,
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Add sx={{ mr: 0.5 }} />
                                            {financeText("Add Cost Line")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh supplier costs")}>
                                            <IconButton
                                                onClick={fetchSupplierCosts}
                                                sx={{
                                                    color: "#FFFFFF",
                                                    border: "1px solid rgba(255,255,255,.18)",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <Card sx={{ ...cardSx, mb: 2.5 }}>
                    <MDBox p={{ xs: 2, md: 2.5 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("Start Date")}
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("End Date")}
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Customer / Supplier")}
                                    value={
                                        selectedParty
                                            ? `${selectedParty.type}-${selectedParty.name}`
                                            : "all"
                                    }
                                    onChange={(event) => {
                                        if (event.target.value === "all") {
                                            setSelectedParty(null);
                                            return;
                                        }

                                        setSelectedParty(
                                            partyOptions.find(
                                                (option) =>
                                                    `${option.type}-${option.name}` ===
                                                    event.target.value,
                                            ) || null,
                                        );
                                    }}
                                >
                                    <MenuItem value="all">{financeText("All Customers / Suppliers")}</MenuItem>
                                    {partyOptions.map((option) => {
                                        const value = `${option.type}-${option.name}`;
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {option.type === "CUSTOMER" ? `${financeText("Customer")} - ${option.name}` : `${financeText("Supplier")} - ${option.name}`}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Invoice Origin")}
                                    value={invoiceOrigin}
                                    onChange={(event) => setInvoiceOrigin(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Origins")}</MenuItem>
                                    <MenuItem value="tms">TMS</MenuItem>
                                    <MenuItem value="manual">{financeText("Manual")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Geographic Scope")}
                                    value={geographicScope}
                                    onChange={(event) => setGeographicScope(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Geographic Scopes")}</MenuItem>
                                    <MenuItem value="DOMESTIC">{financeText("Domestic")}</MenuItem>
                                    <MenuItem value="INTERNATIONAL">{financeText("International")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Transport Mode")}
                                    value={transportMode}
                                    onChange={(event) => setTransportMode(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Transport Modes")}</MenuItem>
                                    {transportModeOptions.map((mode) => (
                                        <MenuItem key={mode} value={mode}>
                                            {financeText(MODE_CONFIG[mode]?.label || mode)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Currency")}
                                    value={currencyFilter}
                                    onChange={(event) => setCurrencyFilter(event.target.value)}
                                    helperText={financeText("Filters which records are listed")}
                                >
                                    <MenuItem value="all">{financeText("All Currencies")}</MenuItem>
                                    {currencyOptions.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Sits next to the record-level "Currency" filter
                                above but does something different: it does not
                                change which cost lines are listed, only the
                                currency the aggregated totals are converted
                                into. */}
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Display Currency")}
                                    value={selectedCurrency}
                                    onChange={(event) => setSelectedCurrency(event.target.value)}
                                    helperText={financeText("Currency used for totals")}
                                >
                                    {CURRENCY_OPTIONS.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Document Type")}
                                    value={documentType}
                                    onChange={(event) => setDocumentType(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Document Types")}</MenuItem>
                                    <MenuItem value="invoice">{financeText("Supplier Cost")}</MenuItem>
                                    <MenuItem value="return_invoice">{financeText("Return Invoice")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Return Status")}
                                    value={returnStatus}
                                    onChange={(event) => setReturnStatus(event.target.value)}
                                >
                                    {returnStatusOptions.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status === "all" ? financeText("All Return Statuses") : financeText(status)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {loading ? (
                    <Grid container spacing={SPACE.rowGap}>
                        <Grid item xs={12} lg={7}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        {/* Same hero + secondary-tiles pattern as the Finance
                            Dashboard, from the shared FinanceUiKit. */}

                        <Grid container spacing={SPACE.rowGap} mt={0.5}>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Cost Lines")}
                                    value={formatNumber(stats.count)}
                                    subtitle={financeText("Filtered supplier cost records")}
                                    icon={<Paid />}
                                    tone="navy"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Approved")}
                                    value={formatNumber(stats.approvedCount)}
                                    subtitle={`${stats.approvalRate.toFixed(1)}% ${financeText("approval rate")}`}
                                    icon={<CheckCircle />}
                                    tone="green"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Buying Rate")}
                                    value={formatCompact(stats.buyingRateTotal, selectedCurrency)}
                                    subtitle={financeText("Purchase cost linked to master jobs")}
                                    icon={<Paid />}
                                    tone="amber"
                                />
                            </Grid>
                        </Grid>



                        <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    alignItems={{
                                        xs: "flex-start",
                                        md: "center",
                                    }}
                                    justifyContent="space-between"
                                    flexDirection={{
                                        xs: "column",
                                        md: "row",
                                    }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDBox display="flex" alignItems="center" gap={1}>
                                            <MDBox
                                                width={38}
                                                height={38}
                                                borderRadius="11px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.blue,
                                                    background: COLORS.blueLight,
                                                    border: "1px solid #B2DDFF",
                                                }}
                                            >
                                                <FilterAlt sx={{ fontSize: 19 }} />
                                            </MDBox>

                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Supplier Cost Filters")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText(
                                                        "Search and segment supplier costs by shipment, supplier, invoice, approval, payment and tolerance."
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={fetchSupplierCosts}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <Refresh sx={{ mr: 0.5 }} />
                                            {financeText("Refresh")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={resetFilters}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <RestartAlt sx={{ mr: 0.5 }} />
                                            {financeText("Reset")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            size="small"
                                            onClick={exportCsv}
                                            sx={{
                                                borderRadius: "10px",
                                                background: `${COLORS.navy} !important`,
                                                color: "#FFFFFF !important",
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Download sx={{ mr: 0.5 }} />
                                            {financeText("Export CSV")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={() => navigate("/invoice/finance/jobs")}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            {financeText("Finance Jobs")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={2.4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Search")}
                                            placeholder={financeText("Shipment, supplier, item...")}
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search
                                                            fontSize="small"
                                                            sx={{ color: COLORS.subtext }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={2.4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Invoice Status")}
                                            value={invoiceStatus}
                                            onChange={(event) => setInvoiceStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.invoice.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Invoice Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={2.4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Approval Status")}
                                            value={approvalStatus}
                                            onChange={(event) =>
                                                setApprovalStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.approval.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Approval Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={2.4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Payment Status")}
                                            value={paymentStatus}
                                            onChange={(event) => setPaymentStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.payment.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Payment Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={2.4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Tolerance")}
                                            value={toleranceStatus}
                                            onChange={(event) =>
                                                setToleranceStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.tolerance.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Tolerance Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={2.4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Document Type")}
                                            value={documentType}
                                            onChange={(event) => setDocumentType(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All Document Types")}</MenuItem>
                                            <MenuItem value="invoice">{financeText("Supplier Cost")}</MenuItem>
                                            <MenuItem value="return_invoice">{financeText("Return Invoice")}</MenuItem>
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={2.4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Return Status")}
                                            value={returnStatus}
                                            onChange={(event) => setReturnStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {returnStatusOptions.map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    {status === "all" ? financeText("All Return Statuses") : financeText(status)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <MDBox
                                    mt={2.2}
                                    p={1.5}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems={{
                                        xs: "flex-start",
                                        md: "center",
                                    }}
                                    justifyContent="space-between"
                                    flexDirection={{
                                        xs: "column",
                                        md: "row",
                                    }}
                                    gap={1}
                                    sx={{
                                        background: "#F9FAFB",
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Showing")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {filteredCostLines.length}
                                        </strong>{" "}
                                        {financeText("of")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {costLines.length}
                                        </strong>{" "}
                                        {financeText("supplier cost / return records")}
                                    </MDTypography>

                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            size="small"
                                            label={`${financeText("Expected")} ${formatCompact(stats.expectedTotal, selectedCurrency)}`}
                                            sx={{
                                                color: COLORS.amber,
                                                background: COLORS.amberLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Master Cost")} ${formatCompact(
                                                stats.masterCostTotal,
                                                selectedCurrency,
                                            )}`}
                                            sx={{
                                                color: COLORS.blue,
                                                background: COLORS.blueLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Additional")} ${formatCompact(
                                                stats.approvedAdditionalCost,
                                                selectedCurrency,
                                            )}`}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Variance")} ${formatCompact(stats.variance, selectedCurrency)}`}
                                            sx={{
                                                color: stats.variance > 0 ? COLORS.red : COLORS.green,
                                                background:
                                                    stats.variance > 0
                                                        ? COLORS.redLight
                                                        : COLORS.greenLight,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>

                                <Divider sx={{ my: 2.5 }} />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root": {
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: "10px",
                                            overflowX: "auto",
                                            boxShadow: "none",
                                        },
                                        "& table": {
                                            minWidth: 2050,
                                            borderCollapse: "separate",
                                            borderSpacing: 0,
                                        },
                                        "& table thead": {
                                            background:
                                                "#F8FAFC",
                                        },
                                        "& table thead th": {
                                            background: "transparent",
                                            color: "#475467 !important",
                                            fontSize: "10px !important",
                                            fontWeight: "800 !important",
                                            letterSpacing: ".085em",
                                            paddingTop: "15px !important",
                                            paddingBottom: "15px !important",
                                            borderBottom: `1px solid ${COLORS.border} !important`,
                                        },
                                        "& table tbody td": {
                                            paddingTop: "14px !important",
                                            paddingBottom: "14px !important",
                                            borderBottom: "1px solid #F0F2F5 !important",
                                            verticalAlign: "middle",
                                        },
                                        "& table tbody tr": {
                                            background: "#FFFFFF",
                                            transition: "background .18s ease, box-shadow .18s ease",
                                        },
                                        "& table tbody tr:nth-of-type(even)": {
                                            background: "#FCFCFD",
                                        },
                                        "& table tbody tr:hover": {
                                            background: "#F5F8FF !important",
                                            boxShadow: "inset 3px 0 0 #175CD3",
                                        },
                                        "& table tbody tr:last-child td": {
                                            borderBottom: "0 !important",
                                        },
                                        "& .MuiTablePagination-root": {
                                            borderTop: `1px solid ${COLORS.border}`,
                                            background: "#FCFCFD",
                                        },
                                    }}
                                >
                                    <DataTable
                                        table={tableData}
                                        canSearch={false}
                                        entriesPerPage
                                        showTotalEntries
                                        isSorted
                                        noEndBorder
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>

                        {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                        <FinanceDetailsSection
                            storageKey="supplierCosts"
                            title={financeText("Detailed analysis")}
                            subtitle={financeText("Charts and breakdowns for the current filters")}
                        >
                            <Grid container spacing={SPACE.rowGap}>
                            <Grid item xs={12} lg={7}>
                                <FinanceHeroStat
                                    label={financeText("Variance")}
                                    value={formatCompact(stats.variance, selectedCurrency)}
                                    tone={stats.variance > 0 ? "red" : "green"}
                                    icon={<WarningAmber />}
                                    meta={financeText("Actual minus expected")}
                                    context={[
                                        { label: financeText("Expected"), value: formatCompact(stats.expectedTotal, selectedCurrency) },
                                        { label: financeText("Actual"), value: formatCompact(stats.actualTotal, selectedCurrency) },
                                        { label: financeText("Master Cost"), value: formatCompact(stats.masterCostTotal, selectedCurrency) },
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <FinanceCard>
                                    <MDBox p={SPACE.cardPaddingLg} display="flex" flexDirection="column" height="100%">
                                        <FinanceSectionHeader
                                            title={getFinanceLanguage() === "tr" ? "Dikkat gerektiren" : "Needs attention"}
                                        />
                                        <MDBox display="flex" flexDirection="column" gap={1.25}>
                                            {[
                                                { label: financeText("Tolerance"), value: formatNumber(stats.exceededCount), sub: `${stats.missingCount} ${financeText("missing invoices")}`, tone: "red" },
                                                { label: financeText("Additional Cost"), value: formatCompact(stats.approvedAdditionalCost, selectedCurrency), sub: `${stats.includedInMasterCount} ${financeText("lines already included")}`, tone: "purple" },
                                            ].map((row) => (
                                                <MDBox
                                                    key={row.label}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    px={1.75}
                                                    py={1.25}
                                                    borderRadius={SPACE.radiusTile}
                                                    sx={{ background: COLORS.muted }}
                                                >
                                                    <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                                        <MDBox width={8} height={8} borderRadius="50%" flexShrink={0} sx={{ background: COLORS[row.tone] }} />
                                                        <MDBox minWidth={0}>
                                                            <MDTypography variant="button" fontWeight="medium" sx={{ color: COLORS.text, display: "block" }}>
                                                                {row.label}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                                                {row.sub}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS[row.tone], flexShrink: 0 }}>
                                                        {row.value}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </MDBox>
                                </FinanceCard>
                            </Grid>
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            {Object.entries(transportSummary).map(([mode, data]) => (
                                <Grid item xs={12} md={4} key={mode}>
                                    <TransportCostCard
                                        mode={mode}
                                        data={data}
                                        totalRecords={filteredCostLines.length}
                                        currency={selectedCurrency}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            <Grid item xs={12} lg={7}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.5}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Supplier Costs by Transport Mode")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText(
                                                        "Expected and actual supplier costs across Ocean, Road and Air"
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.blue,
                                                    background: COLORS.blueLight,
                                                    border: "1px solid #B2DDFF",
                                                }}
                                            >
                                                <Analytics />
                                            </MDBox>
                                        </MDBox>
                                        <ModeCostChart
                                            summary={transportSummary}
                                            currency={selectedCurrency}
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.2}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Shipment Type Mix")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Supplier cost distribution by logistics service")}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.purple,
                                                    background: COLORS.purpleLight,
                                                    border: "1px solid #D9D6FE",
                                                }}
                                            >
                                                <Inventory2 />
                                            </MDBox>
                                        </MDBox>

                                        {shipmentTypeSummary.length ? (
                                            shipmentTypeSummary.map(([type, count], index) => {
                                                const max = shipmentTypeSummary[0]?.[1] || 1;
                                                return (
                                                    <MDBox key={type} mb={1.55}>
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            mb={0.55}
                                                        >
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.text }}
                                                            >
                                                                {index + 1}. {type}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.purple }}
                                                            >
                                                                {count}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={(count / max) * 100}
                                                            sx={{
                                                                height: 7,
                                                                borderRadius: "999px",
                                                                backgroundColor: "#EEF2F6",
                                                                "& .MuiLinearProgress-bar": {
                                                                    borderRadius: "999px",
                                                                    background: COLORS.purple,
                                                                },
                                                            }}
                                                        />
                                                    </MDBox>
                                                );
                                            })
                                        ) : (
                                            <MDBox py={4} textAlign="center">
                                                <MDTypography
                                                    variant="body2"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("No shipment type data available.")}
                                                </MDTypography>
                                            </MDBox>
                                        )}
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                        </FinanceDetailsSection>

                    </>
                )}
            </MDBox>

            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: `1px solid ${COLORS.border}`,
                        boxShadow: "0 24px 64px rgba(16,24,40,.18)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: 3,
                        py: 2.5,
                        background:
                            "#0B1F3A",
                        color: "#FFFFFF",
                    }}
                >
                    <MDBox display="flex" alignItems="center" gap={1.2}>
                        <MDBox
                            width={38}
                            height={38}
                            borderRadius="11px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                background: "rgba(255,255,255,.12)",
                            }}
                        >
                            <Paid />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ color: "#FFFFFF" }}
                            >
                                {financeText("Add Supplier Cost Line")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color: "rgba(255,255,255,.68)",
                                }}
                            >
                                {financeText("Register expected and actual supplier cost details.")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Finance Job ID")}
                                value={costForm.financeJobId}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        financeJobId: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Shipment ID")}
                                value={costForm.shipmentId}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        shipmentId: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Shipment / Job No")}
                                value={costForm.jobNo}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        jobNo: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Service Type")}
                                value={costForm.serviceType}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        serviceType: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Supplier Name")}
                                value={costForm.supplierName}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        supplierName: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Supplier Type")}
                                value={costForm.supplierType}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        supplierType: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="Service Provider">{financeText("Service Provider")}</MenuItem>
                                <MenuItem value="Carrier">{financeText("Carrier")}</MenuItem>
                                <MenuItem value="Customs Broker">{financeText("Customs Broker")}</MenuItem>
                                <MenuItem value="Warehouse">{financeText("Warehouse")}</MenuItem>
                                <MenuItem value="Port / Terminal">{financeText("Port / Terminal")}</MenuItem>
                                <MenuItem value="Other">{financeText("Other")}</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Cost Item")}
                                value={costForm.costItemName}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        costItemName: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Cost Code")}
                                value={costForm.costItemCode}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        costItemCode: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Expected Amount")}
                                value={costForm.expectedAmount}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        expectedAmount: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Actual Invoice Amount")}
                                value={costForm.actualInvoiceAmount}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        actualInvoiceAmount: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Currency")}
                                value={costForm.currency}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        currency: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="EUR">EUR</MenuItem>
                                <MenuItem value="USD">USD</MenuItem>
                                <MenuItem value="TRY">TRY</MenuItem>
                                <MenuItem value="GBP">GBP</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Tolerance %")}
                                value={costForm.toleranceLimitPercent}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        toleranceLimitPercent: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Invoice Status")}
                                value={costForm.invoiceStatus}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        invoiceStatus: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="Expected">{financeText("Expected")}</MenuItem>
                                <MenuItem value="Received">{financeText("Received")}</MenuItem>
                                <MenuItem value="Missing">{financeText("Missing")}</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Approval Status")}
                                value={costForm.approvalStatus}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        approvalStatus: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="Pending">{financeText("Pending")}</MenuItem>
                                <MenuItem value="Approved">{financeText("Approved")}</MenuItem>
                                <MenuItem value="Rejected">{financeText("Rejected")}</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Payment Status")}
                                value={costForm.paymentStatus}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        paymentStatus: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="Not Due">{financeText("Not Due")}</MenuItem>
                                <MenuItem value="Pending">{financeText("Pending")}</MenuItem>
                                <MenuItem value="Paid">{financeText("Paid")}</MenuItem>
                                <MenuItem value="Overdue">{financeText("Overdue")}</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Master Cost Relation")}
                                value={
                                    costForm.includedInMasterCost ? "included" : "additional"
                                }
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        includedInMasterCost: event.target.value === "included",
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="additional">{financeText("Additional Cost")}</MenuItem>
                                <MenuItem value="included">
                                    {financeText("Already Included in Master Cost")}
                                </MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={financeText("Due Date")}
                                InputLabelProps={{ shrink: true }}
                                value={costForm.dueDate}
                                onChange={(event) =>
                                    setCostForm((previous) => ({
                                        ...previous,
                                        dueDate: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2.2,
                        background: "#F9FAFB",
                        borderTop: `1px solid ${COLORS.border}`,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={() => setModalOpen(false)}
                        sx={{ borderRadius: "10px" }}
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        onClick={saveCostLine}
                        sx={{
                            borderRadius: "10px",
                            background: `${COLORS.navy} !important`,
                            color: "#FFFFFF !important",
                            boxShadow: "none",
                        }}
                    >
                        {financeText("Save Cost Line")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default SupplierCosts;